import React, { useState } from "react";
import { api, money } from "@/lib/api";
import { toast } from "sonner";
import { X, Timer, Loader2 } from "lucide-react";

export const HoldModal = ({ product, qty, deposit, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const depositTotal = deposit * qty;
  const balance = Math.max(0, product.price * qty * 1.13 - depositTotal);

  const start = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/orders/hold", {
        items: [{ product_id: product.id, quantity: qty }],
        customer_name: form.name.trim(),
        customer_email: form.email.trim(),
        customer_phone: form.phone.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not start the hold.");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="hold-modal">
        <div className="flex justify-between items-start">
          <div className="inline-flex items-center gap-2 text-brand-navy font-heading text-xl"><Timer className="h-5 w-5 text-brand-red" /> Hold for 24 hours</div>
          <button onClick={onClose} data-testid="hold-modal-close" className="text-slate-500 hover:text-brand-red"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-sm text-slate-600 mt-2">
          Pay a small refundable deposit to reserve <b>{product.product_name}</b> for 24 hours. The deposit is
          credited toward your total — pay the balance online before you pick up.
        </p>
        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-slate-500">Quantity</span><span className="font-bold">{qty}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Deposit now</span><span className="font-bold text-brand-navy" data-testid="hold-deposit-amount">{money(depositTotal)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Balance later (incl. HST)</span><span className="font-bold">{money(balance)}</span></div>
        </div>
        <div className="mt-4 space-y-2">
          <input placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="hold-name" className="w-full h-11 rounded-lg border border-slate-200 px-3" />
          <input type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="hold-email" className="w-full h-11 rounded-lg border border-slate-200 px-3" />
          <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="hold-phone" className="w-full h-11 rounded-lg border border-slate-200 px-3" />
        </div>
        <button onClick={start} disabled={submitting} data-testid="hold-pay-deposit-btn"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-redHover text-white font-black uppercase tracking-wider h-12 rounded-full active:scale-95 transition disabled:opacity-60">
          {submitting ? <><Loader2 className="h-5 w-5 animate-spin" /> Redirecting…</> : `Pay ${money(depositTotal)} deposit & hold`}
        </button>
        <p className="text-[11px] text-slate-400 mt-2 text-center">Card only. If you don't pay the balance within 24 hours, the item is released and the deposit is kept as a no-show fee (we'll email a reminder first).</p>
      </div>
    </div>
  );
};
